import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { observer } from 'mobx-react';
import cc from 'classnames';
import { useTheme, useMediaQuery, Button, IconButton } from '@material-ui/core';
import Table from 'ui/Table/Table';
import Loader from 'ui/Loader/Loader';
import {
  DatePickerRangeModal,
  DownloadStatementModal,
  EmailStatementModal,
  TransactionsFilterModal,
} from 'components/modals';
import api from 'api/api';
import noop from 'utils/noop';
import DashboardCard from 'ui/DashboardCard/DashboardCard';
import AccountSelector from '../Statistics/AccountSelector';
import TransactionHistoryMobileCard from './TransactionHistoryMobileCard/TransactionHistoryMobileCard';
import columns from './columns';
import { ITransactions, ITransactionsResponse } from './TransactionHistory.model';

import styles from './TransactionHistory.module.css';

import filterIcon from 'assets/icons/filter.png';
import letterIcon from 'assets/icons/letter_icon.png';
import creditIcon from 'assets/icons/credit_icon.png';
import mobileMenuIcon from 'assets/icons/navigation_more.png';
import debitIcon from 'assets/icons/debit_icon.png';
import downloadIcon from 'assets/img/download.png';
import calendar from 'assets/img/calendar.png';

const tableIcons = {
  Credit: <img src={creditIcon} alt="credit" />,
  Debit: <img src={debitIcon} alt="debit" />,
};

const TransactionHistory = () => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showMobileStatement, setShowMobileStatement] = useState(false);

  const [isDatePickerModalOpen, setDatePickerModalOpen] = useState<boolean>(false);
  const [selectedDateRange, handleDateRangeChange] = useState([
    new Date().setDate(new Date().getDate() - 30),
    new Date().setDate(new Date().getDate() + 60),
  ]);

  const [startRangeDate, endRangeDate] = selectedDateRange;

  const [transactions, setTransactions] = useState<ITransactions[]>([]);
  const [allTransactions, setAllTransactions] = useState<ITransactions[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);

  const initialFilters = {
    type: 'All',
    from: '0.00',
    to: '100000.00',
    narration: '',
  };

  const [filters, setFilters] = useState(initialFilters);

  const getParsedDate: (date: any, isFormated?: boolean) => string = useCallback(
    // eslint-disable-next-line newline-per-chained-call
    (date, isFormated = false) => {
      if (isFormated) {
        const parsedDate = new Date(date).toJSON()?.slice(0, 10)?.split('-');
        // Parsed date in DD-MM-YYYY format
        return `${parsedDate[2]}-${parsedDate[1]}-${parsedDate[0]}` || '';
      }
      return new Date(date).toJSON()?.slice(0, 10)?.split('-').reverse().join('-') || '';
    },
    [],
  );

  const preparedTableData = (data: ITransactions[]) =>
    data.map((item) => ({
      ...item,
      amount: `${String(item.currency)} ${item.type === 'D' ? '-' : ''} ${Number(item.amount).toFixed(2)}`,
      type: item.type === 'D' ? 'Debit' : 'Credit',
      date: getParsedDate(item.date),
    }));

  const openEmailStatementModal: () => unknown = () => setShowEmailModal(true);
  const closeEmailStatementModal: () => unknown = () => setShowEmailModal(false);

  const openDownloadStatementModal = useCallback(() => setShowDownloadModal(true), []);
  const closeDownloadStatementModal = useCallback(() => setShowDownloadModal(false), []);

  const openFilterModal: () => unknown = () => setShowFilterModal(true);
  const closeFilterModal: () => unknown = () => setShowFilterModal(false);

  const toggleMobileStatement: () => unknown = () => setShowMobileStatement((prev) => !prev);

  const openDatePickerModal: () => unknown = () => setDatePickerModalOpen(true);
  const closeDatePickerModal: () => unknown = () => setDatePickerModalOpen(false);

  const getDateRange: (startDate, endDate) => void = (startDate, endDate) => {
    handleDateRangeChange([startDate, endDate]);
  };

  const changeFilters = (values) => {
    setFilters(values);
  };

  const resetFilters = () => {
    setFilters(initialFilters);
  };

  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const mediumScreen = useMediaQuery(theme.breakpoints.down('lg'));

  useEffect(() => {
    setSubmitting(true);
    const requestBody = {
      startDate: getParsedDate(startRangeDate),
      endDate: getParsedDate(endRangeDate),
      accountNumber: selectedAccount,
    };
    api
      .post('/account/transactions/search', requestBody)
      .then((response: ITransactionsResponse) => {
        if (!response.transactions && response.file) {
          const url = `data:application/pdf;base64,${response.file}`;
          const downloadLink = document.createElement('a');
          downloadLink.href = url;
          downloadLink.download = 'transactions.pdf';
          downloadLink.click();
        } else {
          setTransactions(response.transactions.slice(0, 20));
          setAllTransactions(response.transactions.slice(0, 20));
        }
      })
      .catch((error) => {
        console.error(error);
        setTransactions([]);
      })
      .finally(() => setSubmitting(false));
  }, [startRangeDate, endRangeDate, getParsedDate]);

  useEffect(() => {
    const { from, to, type, narration } = filters;
    if (type !== 'All') {
      setTransactions(allTransactions.filter((item) => item.type === type && item.amount >= from && item.amount <= to));
    } else {
      setTransactions(allTransactions.filter((item) => item.amount >= from && item.amount <= to));
    }
    if (filters.narration) {
      const filterFunc = (item) => item.narration.toLocaleLowerCase().includes(narration.toLocaleLowerCase());
      setTransactions((prev) => prev.filter(filterFunc));
    }
  }, [allTransactions, filters]);

  const header = (
    <div className={styles.header}>
      <div className={styles.titleWrapper}>
        <h2 className={styles.title}>Account Statement</h2>
        {smallScreen && (
          <div className={styles.iconsWrapper}>
            <IconButton
              aria-label="filter type"
              className={cc(styles.button, styles.iconButton)}
              onClick={openFilterModal}
            >
              <img src={filterIcon} alt="filter" />
            </IconButton>
            <IconButton
              aria-label="open menu"
              className={cc(styles.button, styles.mobileMenuButton)}
              onClick={toggleMobileStatement}
            >
              <img src={mobileMenuIcon} alt="menu" />
            </IconButton>
          </div>
        )}
      </div>
      <div className={!smallScreen ? styles.buttonsWrapper : styles.mobileButtonsWrapper}>
        <div className={styles.accountSelectorWrapper}>
          <AccountSelector onAccountChanged={(acc) => setSelectedAccount(acc)} />
        </div>
        <div
          className={styles.datePickerInput}
          onClick={openDatePickerModal}
          onKeyPress={openDatePickerModal}
          role="button"
          tabIndex={0}
        >
          <span className={styles.datePickerInputValue}>
            {`${getParsedDate(selectedDateRange[0])} - ${getParsedDate(selectedDateRange[1])}`}
          </span>
          <img className={styles.datePickerInputIcon} src={calendar} alt="Calendar logo" />
        </div>
        {!mediumScreen && !smallScreen && (
          <>
            <Button
              onClick={openDownloadStatementModal}
              className={cc(styles.button, styles.statementButton)}
              endIcon={<img src={downloadIcon} alt="download" />}
            >
              Download Statement
            </Button>
            <Button
              onClick={openEmailStatementModal}
              className={cc(styles.button, styles.statementButton)}
              endIcon={<img src={letterIcon} alt="letter" />}
            >
              Email Statement
            </Button>
          </>
        )}
        <Button
          className={cc(styles.button, styles.filterButton)}
          endIcon={<img src={filterIcon} alt="filter" />}
          onClick={openFilterModal}
        >
          Filter Type
        </Button>
        {!smallScreen && (
          <div className={styles.iconsWrapper}>
            <IconButton
              aria-label="filter type"
              className={cc(styles.button, styles.iconButton)}
              onClick={openFilterModal}
            >
              <img src={filterIcon} alt="filter" />
            </IconButton>
            <IconButton
              aria-label="open menu"
              className={cc(styles.button, styles.mobileMenuButton)}
              onClick={toggleMobileStatement}
            >
              <img src={mobileMenuIcon} alt="menu" />
            </IconButton>
          </div>
        )}
      </div>
      <EmailStatementModal
        open={showEmailModal}
        onClose={closeEmailStatementModal}
        activeAccountNumber={selectedAccount || ''}
        startDate={getParsedDate(startRangeDate, true)}
        endDate={getParsedDate(endRangeDate, true)}
      />
      <DownloadStatementModal
        open={showDownloadModal}
        onClose={closeDownloadStatementModal}
        activeAccountNumber={selectedAccount || ''}
        startDate={getParsedDate(startRangeDate, true)}
        endDate={getParsedDate(endRangeDate, true)}
      />
      <TransactionsFilterModal
        open={showFilterModal}
        onClose={closeFilterModal}
        changeFilters={changeFilters}
        filters={filters}
        resetFilters={resetFilters}
      />
      <DatePickerRangeModal
        open={isDatePickerModalOpen}
        startRangeDate={selectedDateRange[0]}
        endRangeDate={selectedDateRange[1]}
        onClose={closeDatePickerModal}
        getDateRange={getDateRange}
      />
      {showMobileStatement && (
        <div className={styles.mobileStatement}>
          <Button
            onClick={openEmailStatementModal}
            className={styles.mobileStatementButton}
            startIcon={<img src={letterIcon} alt="letter" />}
          >
            Email Statement
          </Button>
          <Button
            onClick={openDownloadStatementModal}
            className={styles.mobileStatementButton}
            startIcon={<img src={downloadIcon} alt="download" />}
          >
            Download Statement
          </Button>
        </div>
      )}
    </div>
  );

  const emptyMessage = 'There are no transactions.';

  const tableData = useMemo(() => preparedTableData(transactions), [transactions]);

  const renderData = () => {
    if (submitting) {
      return <Loader />;
    }

    if (smallScreen) {
      return <TransactionHistoryMobileCard data={tableData} columns={columns} emptyMessage={emptyMessage} />;
    }

    return (
      <Table columns={columns} data={tableData} emptyTableMessage={emptyMessage} icons={tableIcons} onClick={noop} />
    );
  };

  return (
    <DashboardCard fullHeight>
      {header}
      {renderData()}
    </DashboardCard>
  );
};

export default observer(TransactionHistory);
